<template lang='pug'>
  .cms
    roguebuilder-sidebar(:entity="page" ref="dynamicSidebar")
    loading(v-if="status === 'loading' && !$rogue.appContext" height="75vh")
    err404(v-else-if="status === 'error'")
    .render(v-else-if="page.roguebuilder" @click="checkClick($event)")
      RenderBlock(:product="renderProduct" :block="page.roguebuilder")
      .reviews.main-container(v-if="showReviews")
        Reviews.reviews-about-page(:product="REVIEWS_PRODUCT")
    .cms-html(v-else v-html="page.content")
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onServerPrefetch, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead, type ReactiveHead } from '@unhead/vue'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import RoguebuilderSidebar from '../../components/roguebuilder-sidebar.vue'
import { useCmsStore } from '@/js/stores/cmsStore'
import { useUIStore } from '@/js/stores/uiStore'
import { useRogue } from '@/js/services/Rogue'
import metadata from '@/js/helpers/metadata'
import { messages } from '@/js/app-bridge/messages'
import { ROGUE_APP_SPA_URL_PREFIX } from '@/js/app-bridge/helpers'
import type { CMS } from '@/js/services/RogueTypes'

const props = defineProps<{ pageId?: string }>()

const Reviews = defineAsyncComponent(() => import(/* webpackChunkName: "reviews" */'Src/js/pages/product/components/reviews.vue'))

const REVIEWS_PRODUCT = { sku: 'ROGUE-US' }

// Give the sidebar a beat to render before opening a hash target
const SIDEBAR_HASH_SHOW_DELAY_MS = 100

const cmsStore = useCmsStore()
const uiStore = useUIStore()
const route = useRoute()
const router = useRouter()
const rogue = useRogue()

const dynamicSidebar = useTemplateRef<{ show: (name: string) => void, checkClick: (e: MouseEvent) => void }>('dynamicSidebar')

// The route path without its leading slash - doubles as the product SKU and i18n namespace for CMS pages
const routeKey = computed(() => route.path.substring(1))

const isAppPage = computed(() => route.path.startsWith(ROGUE_APP_SPA_URL_PREFIX))

const renderProduct = computed(() => ({ sku: routeKey.value }))

const id = computed(() => props.pageId || (route.meta.id as number | string | undefined))

const page = computed<Partial<CMS>>(() => {
  if (id.value && cmsStore.cms[id.value]) {
    return cmsStore.cms[id.value]
  }

  const idByUrl = cmsStore.byUrl[route.path]
  if (idByUrl && cmsStore.cms[idByUrl]) {
    return cmsStore.cms[idByUrl]
  }

  const idByIdentifier = id.value ? cmsStore.byIdentifier[id.value] : undefined

  if (idByIdentifier && cmsStore.cms[idByIdentifier]) {
    return cmsStore.cms[idByIdentifier]
  }

  return {}
})

// Start as loaded when the page is already in the store (e.g. hydrating from SSR state),
// so the loading spinner doesn't replace already-rendered content
const status = ref<'loading' | 'loaded' | 'error'>(hasPageData() ? 'loaded' : 'loading')

const showReviews = computed(() => Boolean(page.value.roguebuilder?.props?.showReviews))

const ogImageSource = computed(() => page.value.roguebuilder?.props?.ogImage?.src)
const ogImageAlt = computed(() => page.value.roguebuilder?.props?.ogImage?.alt)
const cmsOgImage = computed(() => getCloudinaryUrl(ogImageSource.value, 1200, 630))

useHead(() => {
  const { title, description } = page.value.meta || {}

  return metadata({
    url         : `${rogue.localizedBaseURL}${route.path}`,
    robots      : isAppPage.value ? 'none' : 'all',
    title       : rogue.__(title, undefined, routeKey.value),
    description : rogue.__(description?.replace(/<[^>]*>/gi, ''), undefined, routeKey.value),
    image       : cmsOgImage.value,
    imageWidth  : cmsOgImage.value ? 1200 : undefined,
    imageHeight : cmsOgImage.value ? 630 : undefined,
    imageAlt    : cmsOgImage.value ? ogImageAlt.value : undefined,
  }, rogue) as ReactiveHead
})

watch(id, () => {
  load()
})

// Watch for this value to reload the page content in the app context
watch(() => uiStore.appLastContentReloadAt, (curr, prev) => {
  if (rogue.isApp() && curr !== undefined && prev !== undefined && curr > prev) {
    cmsStore.forceReload(routeKey.value)
    load()
  }
})

onServerPrefetch(load)

if (!rogue.ssr) {
  load()
}

onMounted(() => {
  // Intercept rg_app pages if user is not in the app and the feature flag is not enabled for that customer.  Feature flag should be limited to isRogue customers.
  if (isAppPage.value && !rogue.appContext && !(rogue.debug || rogue.featureFlags.isOn('invitational_app_display'))) {
    router.replace('/')
  }

  if (route.hash) {
    setTimeout(() => {
      dynamicSidebar.value?.show(route.hash.substring(1))
    }, SIDEBAR_HASH_SHOW_DELAY_MS)
  }
})

function hasPageData() {
  return Boolean(page.value.roguebuilder || page.value.content)
}

function checkClick(e: MouseEvent) {
  dynamicSidebar.value?.checkClick(e)
}

async function load() {
  if (!id.value) {
    return
  }

  if (!hasPageData()) {
    status.value = 'loading'
    await cmsStore.load(id.value, rogue)
  }

  if (rogue.i18n.enabled) {
    const namespace = route.path === '/' ? 'homepage' : routeKey.value
    const loadTranslations = rogue.i18n.loadNamespace(namespace)
    if (loadTranslations) {
      await loadTranslations
    }
  }

  status.value = hasPageData() ? 'loaded' : 'error'

  // rogue.emitToApp touches window, so only emit in the browser
  if (rogue.appContext && !rogue.ssr) {
    const data = {
      url   : `${rogue.localizedBaseURL}${route.fullPath}`,
      title : page.value.title || page.value.metaTitle,
      type  : route.path,
    }

    rogue.emitToApp(messages.PAGE_OPENED, data)
    rogue.emitToApp(messages.DOM_LOADED, {})
  }
}
</script>

<style lang="scss" scoped>
@include mobile(){
  .homepage-featured {
    --tabs-container-padding: 0 0 0 #{$space-s};
  }
}
.cms {
  //probably we would send any custom styles with the JSON and add/remove them
  //similar to how we do in the index
  //BRANDS CMS PAGE STYLES
  :deep(.products-grid) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin: -$space-s;

    @media(min-width: $tablet) {
      justify-content: space-between;
    }

    .item {
      margin: $space-s;
      width: 140px;
      height: 104px;
      overflow: hidden;
    }
  }

  .reviews-about-page:deep() {
    .bv-cv2-cleanslate .bv-core-container-359 .bv-action-bar-header {
      visibility: hidden !important;
      // display: none !important;

      &::after {
        content: 'What People Are Saying About Us' !important;
        visibility: visible !important;
        left: 0;
        position: relative !important;
        display: block !important;
      }
    }
  }
}
</style>

<style lang="scss" scoped target="app">
.platform-ios, .platform-android {
  .cms :deep(.cms-row.main-container) {
    margin: 0;
    padding: $app-gap-default;
  }
}
</style>
